import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { gsap, registerGsap } from '@/lib/animation'
import { siteConfig } from '@/constants/site'

/** Seconds the panel takes to travel one screen height in each direction. */
const COVER = 0.5
const REVEAL = 0.85

const isReduced = (): boolean =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Sweeps the panel down and off the bottom, uncovering the page from the top. */
const sweepAway = (el: HTMLElement): void => {
    gsap.killTweensOf(el)

    gsap.to(el, {
        yPercent: 100,
        duration: REVEAL,
        ease: 'springOut',
        onComplete: () => gsap.set(el, { autoAlpha: 0 })
    })
}

/** Brings the panel down from above so it covers the outgoing page. */
const sweepOver = (el: HTMLElement): gsap.core.Tween => {
    gsap.killTweensOf(el)
    gsap.set(el, { autoAlpha: 1, yPercent: -100 })

    return gsap.to(el, {
        yPercent: 0,
        duration: COVER,
        ease: 'power2.inOut'
    })
}

/**
 * Full-screen panel that sweeps downward across every page entrance.
 *
 * On first load the markup is server-rendered already covering the viewport,
 * so there is never a flash of the finished page, then it sweeps off the
 * bottom. Because it travels downward the page is uncovered from the top, which
 * lands the hero's `data-reveal-load` sequence exactly as it comes into view.
 *
 * On client-side navigation it drops in from above, holds while the new page
 * mounts and the scroll resets, then sweeps away the same way, so no page ever
 * simply appears.
 *
 * `globals.css` keeps the panel hidden unless `.js-anim` is set, and carries a
 * delayed CSS fallback that clears it even if this component never runs, so a
 * no-JS, reduced-motion or failed-bundle visitor is never left behind it.
 */
export const PageTransition: FC = (): ReactNode => {
    const router = useRouter()
    const panel = useRef<HTMLDivElement>(null)
    const mark = useRef<HTMLSpanElement>(null)
    const covering = useRef<gsap.core.Tween | null>(null)
    const current = useRef(router.asPath)

    useEffect(() => {
        current.current = router.asPath
    }, [router.asPath])

    // Opening sequence, mount only.
    useEffect(() => {
        const el = panel.current

        if (!el) return

        registerGsap()

        if (isReduced()) {
            gsap.set(el, { autoAlpha: 0 })

            return
        }

        // The CSS fallback only exists for the case where this never runs, so
        // the first thing to do is take the panel off it.
        el.style.animation = 'none'

        const opening = gsap.timeline({ delay: 0.15 })

        opening
            .set(el, { autoAlpha: 1, yPercent: 0 })
            .to(mark.current, { opacity: 0, duration: 0.45, ease: 'power2.out' }, 0)
            .to(
                el,
                {
                    yPercent: 100,
                    duration: REVEAL,
                    ease: 'springOut',
                    onComplete: () => gsap.set(el, { autoAlpha: 0 })
                },
                0.1
            )

        return () => {
            opening.kill()
        }
    }, [])

    // Navigation sequence.
    useEffect(() => {
        const el = panel.current

        if (!el) return

        const onStart = (url: string): void => {
            if (isReduced() || url === current.current) return

            el.style.animation = 'none'
            gsap.set(mark.current, { opacity: 0 })

            covering.current = sweepOver(el)
        }

        const onSettled = (): void => {
            if (isReduced()) return

            const tween = covering.current

            // The new page usually mounts before the panel has finished
            // covering; waiting on that tween keeps the two halves of the
            // sweep from overlapping into a stutter.
            if (tween && tween.isActive()) {
                tween.eventCallback('onComplete', () => sweepAway(el))

                return
            }

            sweepAway(el)
        }

        router.events.on('routeChangeStart', onStart)
        router.events.on('routeChangeComplete', onSettled)
        router.events.on('routeChangeError', onSettled)

        return () => {
            router.events.off('routeChangeStart', onStart)
            router.events.off('routeChangeComplete', onSettled)
            router.events.off('routeChangeError', onSettled)
        }
    }, [router.events])

    return (
        <div
            ref={panel}
            aria-hidden='true'
            className='page-curtain pointer-events-none fixed inset-0 z-[200] items-center justify-center bg-ink'
        >
            <span
                ref={mark}
                className='font-heading text-[64px] font-medium leading-none tracking-[-0.04em] text-cream md:text-[110px]'
            >
                {siteConfig.name}
            </span>
        </div>
    )
}
